import React, { useState, useEffect, useRef } from 'react';
import { useWeb3, PRO_PERKS } from '../Web3Context';
import { useLang } from '../LangContext';
import { WalletModal } from './Web3Modal';

const WEB3_TEXT = {
  en: {
    badge: 'Web3 Exclusive',
    headline: 'Unlock Pro with Your NFT',
    sub: 'Hold a VidGet Pro NFT in your Web3 wallet to unlock premium features — no subscription, no recurring fees. Own your access forever.',
    ctaConnect: 'Connect Wallet',
    ctaConnected: 'View Pro Perks',
    ctaMint: 'Mint Pro NFT',
    stats: [
      { num: '2,841', label: 'Pro Holders' },
      { num: '0.05 ETH', label: 'Mint Price' },
      { num: '∞', label: 'Lifetime Access' },
    ],
    perksTitle: 'Everything in Pro',
  },
  id: {
    badge: 'Eksklusif Web3',
    headline: 'Buka Pro dengan NFT Kamu',
    sub: 'Pegang VidGet Pro NFT di wallet Web3-mu untuk membuka fitur premium — tanpa langganan, tanpa biaya bulanan. Akses selamanya.',
    ctaConnect: 'Hubungkan Wallet',
    ctaConnected: 'Lihat Keuntungan Pro',
    ctaMint: 'Mint Pro NFT',
    stats: [
      { num: '2.841', label: 'Pemegang Pro' },
      { num: '0.05 ETH', label: 'Harga Mint' },
      { num: '∞', label: 'Akses Seumur Hidup' },
    ],
    perksTitle: 'Semua Fitur Pro',
  },
};

export default function Web3Banner() {
  const { wallet, isProHolder } = useWeb3();
  const { lang } = useLang();
  const t = WEB3_TEXT[lang];
  const [showModal, setShowModal] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const perks = PRO_PERKS[lang] || PRO_PERKS.en;

  return (
    <>
      <section id="web3" ref={ref} style={{
        padding: '6rem 1.5rem',
        background: 'var(--bg)',
        borderTop: '1px solid rgba(255,255,255,0.04)',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Purple glow BG */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '700px', height: '400px',
          background: 'radial-gradient(ellipse, rgba(108,71,255,0.07) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '3rem', alignItems: 'center',
          }}>
            {/* Left: copy */}
            <div style={{
              opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.6s, transform 0.6s',
            }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '7px',
                background: 'rgba(108,71,255,0.1)', border: '1px solid rgba(108,71,255,0.25)',
                borderRadius: '99px', padding: '4px 14px', marginBottom: '1.25rem',
              }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#A855F7" strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
                <span style={{ fontSize: '11px', color: '#A855F7', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase' }}>
                  {t.badge}
                </span>
              </div>

              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
                fontWeight: 800, letterSpacing: '-0.025em',
                color: 'var(--text)', marginBottom: '1rem', lineHeight: 1.1,
              }}>
                {t.headline}
              </h2>
              <p style={{
                fontSize: '14px', color: 'var(--text2)', fontWeight: 300,
                lineHeight: 1.75, marginBottom: '2rem', maxWidth: '420px',
              }}>
                {t.sub}
              </p>

              {/* Stats row */}
              <div style={{ display: 'flex', gap: '2rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
                {t.stats.map(({ num, label }) => (
                  <div key={label}>
                    <p style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 700, color: '#A855F7' }}>{num}</p>
                    <p style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '2px' }}>{label}</p>
                  </div>
                ))}
              </div>

              {/* CTA buttons */}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={() => setShowModal(true)} style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  background: 'linear-gradient(135deg, #6C47FF, #A855F7)',
                  color: 'white', border: 'none', borderRadius: '10px',
                  padding: '11px 22px', fontSize: '14px', fontWeight: 500,
                  fontFamily: 'var(--font-body)', cursor: 'pointer',
                  transition: 'opacity 0.15s, transform 0.1s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <rect x="2" y="7" width="20" height="14" rx="2"/>
                    <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
                  </svg>
                  {wallet ? t.ctaConnected : t.ctaConnect}
                </button>

                {!isProHolder && (
                  <button style={{
                    background: 'transparent', color: '#A855F7',
                    border: '1px solid rgba(108,71,255,0.3)',
                    borderRadius: '10px', padding: '11px 22px',
                    fontSize: '14px', fontWeight: 500, fontFamily: 'var(--font-body)',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(108,71,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(108,71,255,0.5)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(108,71,255,0.3)'; }}
                  >
                    {t.ctaMint} ↗
                  </button>
                )}
              </div>
            </div>

            {/* Right: NFT card + perks */}
            <div style={{
              opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.6s 0.15s, transform 0.6s 0.15s',
            }}>
              {/* NFT Card */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(108,71,255,0.15), rgba(168,85,247,0.08))',
                border: '1px solid rgba(108,71,255,0.25)',
                borderRadius: '16px', padding: '1.5rem', marginBottom: '1rem',
                position: 'relative', overflow: 'hidden',
              }}>
                {/* NFT visual */}
                <div style={{
                  height: '140px', borderRadius: '10px', marginBottom: '1rem',
                  background: 'linear-gradient(135deg, #1a0a33, #0d0020)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  position: 'relative', overflow: 'hidden',
                  border: '1px solid rgba(108,71,255,0.2)',
                }}>
                  {/* Floating hexagon shapes */}
                  {[0,1,2].map(i => (
                    <div key={i} style={{
                      position: 'absolute',
                      width: i === 0 ? '70px' : i === 1 ? '45px' : '30px',
                      height: i === 0 ? '70px' : i === 1 ? '45px' : '30px',
                      borderRadius: '12px',
                      background: `rgba(108,71,255,${0.15 - i * 0.04})`,
                      border: '1px solid rgba(108,71,255,0.3)',
                      top: i === 0 ? '50%' : i === 1 ? '25%' : '65%',
                      left: i === 0 ? '50%' : i === 1 ? '70%' : '20%',
                      transform: `translate(-50%, -50%) rotate(${15 + i * 20}deg)`,
                      animation: `floatNft ${2 + i * 0.7}s ease-in-out infinite alternate`,
                    }} />
                  ))}
                  <div style={{
                    width: '52px', height: '52px', borderRadius: '14px',
                    background: 'linear-gradient(135deg, #6C47FF, #A855F7)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative', zIndex: 1,
                    boxShadow: '0 0 30px rgba(108,71,255,0.4)',
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5">
                      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                    </svg>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div>
                    <p style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: 700, color: 'var(--text)' }}>VidGet Pro</p>
                    <p style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '2px' }}>ERC-721 · Ethereum</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: '#A855F7' }}>0.05 ETH</p>
                    <p style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '2px' }}>Mint Price</p>
                  </div>
                </div>

                {wallet && isProHolder && (
                  <div style={{
                    background: 'rgba(108,71,255,0.15)', border: '1px solid rgba(108,71,255,0.3)',
                    borderRadius: '8px', padding: '7px 12px',
                    display: 'flex', alignItems: 'center', gap: '6px',
                  }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#A855F7' }} />
                    <span style={{ fontSize: '12px', color: '#A855F7', fontWeight: 500 }}>NFT detected in wallet</span>
                  </div>
                )}
              </div>

              {/* Perks list */}
              <div style={{
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: '12px', padding: '1rem',
              }}>
                <p style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text2)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '10px' }}>
                  {t.perksTitle}
                </p>
                {perks.map((perk, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: i < perks.length - 1 ? '8px' : 0 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#A855F7" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    <span style={{ fontSize: '13px', color: 'var(--text2)' }}>{perk}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {showModal && <WalletModal onClose={() => setShowModal(false)} />}

      <style>{`
        @keyframes floatNft {
          from { transform: translate(-50%, -50%) rotate(15deg) translateY(0); }
          to   { transform: translate(-50%, -50%) rotate(15deg) translateY(-8px); }
        }
      `}</style>
    </>
  );
}
